import {capitalize, I18nIndividual as GedcomIndividual, Sex} from "../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {GedcomLanguage as Language} from "../model/gedcom-language"
import {JsonGedcomData, gedcomEntriesToJson} from "../topola"
import {JsonIndi, JsonFam} from "../topola/"
import {parse as parseGedcom} from "parse-gedcom"
import assert from "assert"
import {toDateOrYear} from "../utils/date-utils"


/* ------------------------------------------------------------------------------------------------------------------ */
/* Edit GEDCOM                                                                                                        */
/* ------------------------------------------------------------------------------------------------------------------ */

export function gedcomToCreator(gedcom: string, allLanguages: Language[]) {
    const gedcomEntries = parseGedcom(gedcom, allLanguages)
    console.log("Starting import of individuals and families...")
    const jsonGedcomEntries = gedcomEntriesToJson(gedcomEntries, allLanguages)
    const individualsData: GedcomIndividual[] = jsonGedcomEntries.indis.map(indi => jsonToCreatorIndividual(indi))
    const familiesData: GedcomFamily[] = jsonGedcomEntries.fams.map(jsonToCreatorFamily)
    attachIndiParents(individualsData, jsonGedcomEntries)  // Postprocess families to assign parents
    assert(individualsData.length === jsonGedcomEntries.indis.length, "Missing individuals")
    assert(familiesData.length === jsonGedcomEntries.fams.length, "Missing families")
    return { individualsData, familiesData }
}

/**
 * Map from Topola JSON to Creator record.
 */
function jsonToCreatorIndividual(jsonIndi: JsonIndi): GedcomIndividual {
    const nameParts = (jsonIndi.firstName ?? "").trim().split(/\s+/)
    return new GedcomIndividual({
        id: jsonIndi.id,
        enabled: jsonIndi.enabled,
        sex: jsonIndi.sex as Sex,
        givenName: nameParts.length > 1 ? nameParts[0] : jsonIndi.firstName,
        // Full name pattern locale-specific columns
        middleName: nameParts.length > 1 ? nameParts.slice(1).join(" ") : undefined,
        surname1: capitalize(jsonIndi.lastName),
        surname2: jsonIndi.maternalSurname,
        nickname: undefined,
        // --
        birthDate: jsonIndi.birth?.date ? toDateOrYear(jsonIndi.birth.date) : undefined,
        birthPlace: jsonIndi.birth?.place,
        deceaseDate: jsonIndi.death?.date ? toDateOrYear(jsonIndi.death.date) : undefined,
        deceasePlace: jsonIndi.death?.place,
        notes: jsonIndi.notes && jsonIndi.notes.length > 0 ? jsonIndi.notes.join(". ") : undefined,
        // GEDCOM custom tags
        ethnicity: jsonIndi.ethnicity,
        languages: jsonIndi.languages,
        // GEDCOM bindings
        numberOfChildren: jsonIndi.numberOfChildren ?? 0,
        familyChild: jsonIndi.famc,
        numberOfMarriages: jsonIndi.numberOfMarriages ?? 0,
        familySpouses: new Set(jsonIndi.fams),
    })
}

/**
 * Map from Topola JSON to Creator record.
 */
function jsonToCreatorFamily(jsonFam: JsonFam): GedcomFamily {
    return new GedcomFamily({
        id: jsonFam.id,
        enabled: jsonFam.enabled,
        husbandId: jsonFam.husb!,
        wifeId: jsonFam.wife!,
        marriageDate: jsonFam.marriage?.date ? toDateOrYear(jsonFam.marriage.date) : undefined,
        marriagePlace: jsonFam.marriage?.place,
        notes: jsonFam.marriage?.notes ? jsonFam.marriage.notes.join(". ") : undefined,
        // GEDCOM bindings
        children: new Set(jsonFam.children)
    })
}

/**
 * Postprocess families and reattach parents to children.
 */
export function attachIndiParents(individualsData: GedcomIndividual[], jsonGedcomEntries: JsonGedcomData) {
    console.log("Wiring parent-children bindings...")
    const individualsMap = new Map(individualsData.map(indi => [indi.id, indi]))
    for (const fam of jsonGedcomEntries.fams) {
        const husband = individualsMap.get(fam.husb!)
        const wife = individualsMap.get(fam.wife!)
        husband!.numberOfMarriages = (husband!.numberOfMarriages ?? 0) + 1
        wife!.numberOfMarriages = (wife!.numberOfMarriages ?? 0) + 1
        if (fam.children) {
            for (const childId of fam.children) {
                const child = individualsData.find(i => i.id === childId)
                if (child) {
                    child.fatherId = fam.husb
                    child.motherId = fam.wife
                    child.familyChild = fam.id
                    husband!.numberOfChildren = (husband!.numberOfChildren ?? 0) + 1
                    wife!.numberOfChildren = (wife!.numberOfChildren ?? 0) + 1
                }
            }
        }
    }
}